import {
  BRAND, extractRuns, faceOf, lettersFor, scoreSeat, scoreTotal, standings, validateGrid,
  type Coord, type GameEvent, type Letter, type ScoreBreakdown, type WireState,
} from '@nt2/engine';
import { audio } from '../audio/engine.js';
import { lookupDefinition } from '../dict/loader.js';
import { dismissHint, hintDismissed, prefs, PREFS_CHANGED } from '../state/prefs.js';
import type { GameStore } from '../state/store.js';
import {
  fitLayout, layoutOf, LayoutHistory, moveTile, swapTiles, type Layout, type Spot,
} from './arrange.js';
import { Board } from './board.js';
import { recordFrom, saveGame } from '../state/history.js';
import { countingDown } from './countdown.js';
import { Coach, HINTS, type Hint } from './coach.js';
import { openSettings } from './dialogs.js';
import { openHowToPlay } from './demo.js';
import { gameTourSteps, runTour, type TourHandle } from './tour.js';
import { icon } from './icons.js';
import { announce, clear, el, on, setText } from './dom.js';
import { confetti, effectsLayer, ring, sweep } from './effects.js';
import { confirmModal, openModal } from './modal.js';
import { resultsPanel, seriesPanel, SidePanel } from './panels.js';
import { reckoning } from './reckoning.js';
import { Rack } from './rack.js';
import { toast } from './toast.js';

export interface AppCallbacks {
  onOpenMatchOptions(): void;
  onLeave(): void;
}

/**
 * The playing screen.
 *
 * Holds no rules of its own: it reads the authoritative state out of the store,
 * turns input into actions, and turns events into sound and motion.
 */
/** How long an untouched board waits before suggesting how to start. */
const FIRST_TIP_AFTER_MS = 20_000;

/** The actions that rearrange your own letters, and so can be undone. */
const LAYOUT_ACTIONS = new Set(['place', 'recall', 'recallAll', 'moveGroup', 'reorderRack', 'arrange']);

export class App {
  readonly root: HTMLElement;
  readonly board: Board;
  readonly rack: Rack;
  readonly side: SidePanel;
  readonly coach = new Coach();

  #stats: Record<string, HTMLElement> = {};
  #checkBtn: HTMLButtonElement;
  #watchBanner: HTMLElement;
  #lastVersion = -1;
  #lastRound = 0;
  #resultsShown = false;
  #closeResults: (() => void) | null = null;
  #clockTimer = 0;

  constructor(private store: GameStore, private cb: AppCallbacks) {
    this.board = new Board({
      onPlace: (tileId, to) => this.#act({ t: 'place', tileId, to }),
      onRecall: (tileId) => this.#act({ t: 'recall', tileId }),
      onMoveGroup: (moves) => this.#act({ t: 'moveGroup', moves }),
      onSwap: (a, b) => this.#swap(a, b),
      onDragOutside: (tileId, x, y, released) => {
        const slot = this.rack.slotAt(x, y, tileId);
        if (!released) { this.rack.showSlot(slot); return slot !== null; }
        this.rack.showSlot(null);
        if (!slot) return false;
        if (slot.kind === 'swap') this.#swap(tileId, slot.tileId);
        else this.#move(tileId, { rack: slot.index });
        audio.play('drop');
        return true;
      },
      onDirection: () => this.#syncTools?.(),
      onAssignBlank: (tileId, letter) => this.#act({ t: 'assignBlank', tileId, letter }),
      takeFromRack: (letter) => this.rack.take(letter),
      showStatus: () => this.#statusVisible(),
      runStatus: () => this.store.state.report?.runs ?? [],
      inferredBlanks: () => this.store.state.report?.inferred ?? new Map(),
    });
    this.board.onBlankRequested = (tileId) => this.#promptBlank(tileId);

    this.rack = new Rack({
      onPlace: (tileId, to) => this.#act({ t: 'place', tileId, to }),
      onSwap: (a, b) => this.#swap(a, b),
      onMove: (tileId, to) => this.#move(tileId, to),
      onReorder: (order) => this.#act({ t: 'reorderRack', order }),
      onDumpRequest: (tileId) => void this.#confirmDump(tileId),
      onCall: () => this.#declare(),
      onStuck: (stuck) => {
        audio.unlock();
        this.#act({ t: 'stuck', stuck });
        audio.play('select');
      },
    }, this.board);

    this.side = new SidePanel({
      onWatch: (seatId) => {
        this.store.watch(seatId);
        if (seatId) this.side.toggle(false);
      },
      onLeave: () => void this.#confirmLeave(),
      onEndMatch: () => void this.#confirmEndMatch(),
      onConcede: () => void this.#confirmConcede(),
    });

    this.#checkBtn = el('button', {
      class: 'btn btn--sm', type: 'button',
      // "Check" on its own reads like a to-do item. It is a question put to
      // the word list, and the label and the tooltip both have to say so.
      title: 'Ask the word list whether the words on your grid are real (/). ' +
             'These are rationed on purpose — without a limit the game becomes ' +
             'typing letters until something turns green.',
    }, el('span', { class: 'check__long', text: 'Is it a word?' }),
       // On a phone: shaped like the counters beside it — the number left on
       // top, what it is for underneath — but outlined as a button. It was a
       // magnifier and a number, which read as a search box and was not found.
       el('span', { class: 'check__short' },
          el('b', { class: 'check__n' }), el('span', { class: 'check__what', text: 'Is it a word?' }))) as HTMLButtonElement;
    on(this.#checkBtn, 'click', () => this.#check());

    this.#watchBanner = el('div', { class: 'chip chip--warn' });
    this.#watchBanner.hidden = true;

    this.root = el('div', { class: 'shell' },
      this.#topbar(),
      el('div', { class: 'main' },
        this.board.root,
        this.side.scrim,
        this.side.root),
      this.rack.root);

    const main = this.root.querySelector('.main') as HTMLElement;
    effectsLayer(main);
    main.append(this.#switchEl);
    main.insertBefore(this.#toolbar(), this.side.scrim);
    // Between the board and the rack, in the flow, rather than floating over
    // the bottom of the board — which is exactly where a player is building,
    // so it covered their own letters.
    this.root.insertBefore(this.coach.root, this.rack.root);
    this.coach.watchForActivity();

    // Kept, so a screen that has been torn down stops reacting to the store.
    // A destroyed App that still re-rendered could reopen its own results
    // dialog over the lobby, and worse.
    this.#unsubscribe = this.store.subscribe(() => this.render());
    this.#bindShortcuts();
    const onPrefs = (): void => { this.board.refreshCursor(); this.#syncTools?.(); };
    window.addEventListener(PREFS_CHANGED, onPrefs);
    this.#offPrefs = () => window.removeEventListener(PREFS_CHANGED, onPrefs);
    // Both, every second. The call button's lockout expires on a wall clock,
    // not on a state change — with only #renderStats here it stayed reading
    // LOCKED until the server next said something, which in a stalled solo
    // game is never.
    this.#clockTimer = window.setInterval(() => {
      this.#renderStats();
      this.#renderCall();
      if (!this.#hasMoved) this.#renderHints();
      this.#nudgeClock();
    }, 1000);
  }

  #unsubscribe: (() => void) | null = null;

  #offPrefs: (() => void) | null = null;

  destroy(): void {
    this.#offPrefs?.();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#tour?.stop();
    this.coach.destroy();
    clearInterval(this.#clockTimer);
    this.#closeResults?.();
    this.#closeResults = null;
  }

  #lastNudge = 0;

  /**
   * Ask the authority to advance its clock.
   *
   * A Durable Object only runs when something arrives, so a timed rule in a
   * silent room would fire late or never. Sent only while a timed rule is
   * actually armed, and no more than every four seconds — the local transport
   * has its own ticker and needs none of this.
   */
  #nudgeClock(): void {
    const wire = this.store.wire;
    if (!wire || wire.phase !== 'playing') return;
    if (this.store.transport?.kind !== 'remote') return;
    if (wire.forcedDrawAt === null && wire.config.timeLimitMs <= 0) return;
    const now = Date.now();
    if (now - this.#lastNudge < 4000) return;
    this.#lastNudge = now;
    this.store.send({ t: 'tick' } as never);
  }

  // -------------------------------------------------------------------
  // Chrome
  // -------------------------------------------------------------------

  #topbar(): HTMLElement {
    /**
     * `secondary` stats are hidden on a phone, where the bar cannot hold them.
     * Bag and hand are the two numbers a player actually races on; rounds and
     * elapsed time move into the players panel rather than being dropped.
     */
    const mk = (id: string, label: string, title: string, secondary = false): HTMLElement => {
      const v = el('b', { text: '—' });
      this.#stats[id] = v;
      return el('div', {
        class: `stat stat--${id}` + (secondary ? ' stat--secondary' : ''), title,
      }, v, el('span', { text: label }));
    };
    const settings = el('button', {
      class: 'btn btn--icon btn--ghost', type: 'button', 'aria-label': 'Settings',
    }, icon('settings'));
    on(settings, 'click', () => openSettings());
    const help = el('button', {
      class: 'btn btn--icon btn--ghost', type: 'button', 'aria-label': 'How to play',
      title: 'Walk me round the screen, watch a demonstration, or read the rules',
    }, icon('help'));
    on(help, 'click', () => this.#openHelp());
    const players = el('button', {
      class: 'btn btn--icon btn--ghost', type: 'button',
      'aria-label': 'Players and activity',
      title: 'Who is playing, what has happened, and whose grid to watch',
    }, icon('players'));
    on(players, 'click', () => this.side.toggle());
    /*
     * The way out.
     *
     * Leaving was only in the players panel, which is not where anybody looks
     * for it: a cross in the corner is what every other screen on a phone
     * uses, and burying the exit in a list of names made it feel as though
     * there were not one.
     */
    const exit = el('button', {
      class: 'btn btn--icon btn--ghost topbar__exit', type: 'button',
      'aria-label': 'Leave or end the match', title: 'Leave or end the match',
    }, icon('close'));
    on(exit, 'click', () => void this.promptExit());

    /*
     * The mark, top left.
     *
     * The same tile as the app icon and the front page. The word beside it
     * still gives way on a narrow screen — it has to, the bar cannot hold
     * everything — but the tile stays: a game with nothing in the corner
     * looks like a page that failed to finish loading.
     */
    const mark = el('div', { class: 'topbar__mark', 'aria-hidden': 'true', title: BRAND.name },
      el('span', { class: 'topbar__markLetter', text: 'P' }));
    return el('header', { class: 'topbar' },
      mark,
      el('div', { class: 'topbar__brand', text: BRAND.name }),
      this.#watchBanner,
      el('div', { class: 'topbar__stats' },
        mk('bag', 'bag', 'Letters left in the bag'),
        mk('rounds', 'rounds', 'Draw rounds the bag can still pay for', true),
        mk('hand', 'hand', 'Letters in your hand'),
        mk('score', 'score', 'Your score so far: letters in words, less letters ' +
          'you have not used. Crossings count twice.'),
        mk('tocome', 'to come', 'Letters you will still be dealt this match'),
        mk('clock', 'time', 'Time played', true),
        this.#checkBtn,
        players, help, settings, exit));
  }

  #toolbar(): HTMLElement {
    const mkTool = (
      name: Parameters<typeof icon>[0], title: string, fn: () => void,
    ): HTMLButtonElement => {
      const b = el('button', {
        class: 'btn btn--icon', type: 'button', title, 'aria-label': title,
      }, icon(name)) as HTMLButtonElement;
      on(b, 'click', fn);
      return b;
    };
    // Pan is the resting state, and holding still on empty board draws a
    // freehand selection from it. The other two are for anyone who would
    // rather not wait for the hold: freehand straight away, or a rectangle.
    const pan = mkTool('pan', 'Move the board (1)', () => { this.board.setTool('pan'); sync(); });
    const lasso = mkTool('lasso', 'Draw round letters to select them (2)', () => {
      this.board.setTool('lasso'); sync();
    });
    const select = mkTool('select', 'Select a rectangle (3)', () => {
      this.board.setTool('select'); sync();
    });
    /*
     * Which way a tapped letter goes. It could only be changed with Space or
     * Tab, which a phone does not have; a tester asked for it by name. Tapping
     * the cursor square flips it too.
     */
    const dir = mkTool('across', '', () => {
      this.board.setDirection(this.board.direction === 'across' ? 'down' : 'across');
    });
    dir.classList.add('toolbar__dir');
    const undo = mkTool('undo', 'Undo (Ctrl+Z)', () => this.undo());
    const redo = mkTool('redo', 'Redo (Ctrl+Shift+Z)', () => this.redo());
    this.#undoBtn = undo;
    this.#redoBtn = redo;
    const zoomIn = mkTool('zoomIn', 'Zoom in (=)', () => this.board.zoomBy(1.2));
    const zoomOut = mkTool('zoomOut', 'Zoom out (-)', () => this.board.zoomBy(1 / 1.2));
    // A phone pinches; the buttons only cost it room it does not have.
    zoomIn.classList.add('toolbar__zoom');
    zoomOut.classList.add('toolbar__zoom');
    const sync = (): void => {
      lasso.setAttribute('aria-pressed', String(this.board.tool === 'lasso'));
      select.setAttribute('aria-pressed', String(this.board.tool === 'select'));
      pan.setAttribute('aria-pressed', String(this.board.tool === 'pan'));
      const across = this.board.direction === 'across';
      dir.replaceChildren(icon(across ? 'across' : 'down'));
      const t = across
        ? 'Tapped letters go across. Tap to send them down (Space)'
        : 'Tapped letters go down. Tap to send them across (Space)';
      dir.title = t;
      dir.setAttribute('aria-label', t);
      dir.dataset.dir = this.board.direction;
      this.#syncUndo();
    };
    this.#syncTools = sync;
    sync();
    return el('div', { class: 'toolbar' },
      pan, lasso, select, dir, undo, redo,
      zoomIn, zoomOut,
      mkTool('centre', 'Centre on my words (0)', () => this.board.centreOnContent()),
      // Starting over is a normal move in this game — a layout that has gone
      // wrong is often quicker to rebuild than to unpick — so it needs to be
      // one press, but it confirms because it undoes a lot of work.
      mkTool('clear', 'Take everything back to the rack', () => void this.#confirmClear()));
  }

  // -------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------

  /**
   * Attach our seat id and send.
   *
   * Anything that rearranges the player's own letters is remembered first, as
   * the layout before it, so it can be undone.
   */
  #act(partial: Record<string, unknown>, remember = true): void {
    const seatId = this.store.wire?.youSeatId;
    if (!seatId) return;
    // During the start countdown the server refuses every move; not sending
    // them keeps a keen player's letters from jumping back.
    if (countingDown()) return;
    const seat = this.store.seat;
    if (remember && seat && LAYOUT_ACTIONS.has(String(partial['t']))) {
      this.#history.record(layoutOf(seat));
    }
    this.store.send({ seatId, ...partial } as never);
    this.#syncUndo();
  }

  #arrange(layout: Layout | null, remember = true): void {
    if (!layout) { audio.play('invalid'); return; }
    this.#act({ t: 'arrange', grid: layout.grid, rack: layout.rack }, remember);
  }

  /** Two letters trade places — rack or grid, either way round. */
  #swap(a: string, b: string): void {
    const seat = this.store.seat;
    if (!seat) return;
    this.board.clearSelection();
    this.#arrange(swapTiles(seat, a, b));
    audio.buzz(8);
  }

  #move(tileId: string, to: Spot): void {
    const seat = this.store.seat;
    if (!seat) return;
    this.#arrange(moveTile(seat, tileId, to));
  }

  // ---- undo and redo ---------------------------------------------------

  #history = new LayoutHistory();
  /** Which match the history belongs to: tile ids start again in a rematch. */
  #historyFor = '';
  #undoBtn: HTMLButtonElement | null = null;
  #redoBtn: HTMLButtonElement | null = null;

  /** Put your letters back as they were before the last change. */
  undo(): void { this.#travel('undo'); }
  redo(): void { this.#travel('redo'); }

  #travel(way: 'undo' | 'redo'): void {
    const seat = this.store.seat;
    if (!seat || this.store.wire?.phase !== 'playing') return;
    const now = layoutOf(seat);
    const to = way === 'undo' ? this.#history.undo(now) : this.#history.redo(now);
    if (!to) { audio.play('invalid'); this.#syncUndo(); return; }
    this.board.clearSelection();
    this.#arrange(fitLayout(seat, to), false);
    audio.play('drop');
    announce(way === 'undo' ? 'Undone' : 'Redone');
  }

  #syncUndo(): void {
    const playing = this.store.wire?.phase === 'playing';
    if (this.#undoBtn) this.#undoBtn.disabled = !playing || !this.#history.canUndo;
    if (this.#redoBtn) this.#redoBtn.disabled = !playing || !this.#history.canRedo;
  }

  #declare(): void {
    audio.unlock();
    // After the match the same button brings the final table back.
    if (this.store.wire?.phase === 'finished') { this.#showResults(); return; }
    this.#act({ t: 'declare' });
  }

  /** Leaving is the one thing that must always be possible. */
  /**
   * The way out, offering all of the ways out.
   *
   * Leaving, bowing out and ending the match for everybody are three different
   * things with three different consequences, and a single X cannot mean all
   * three. So the X asks, spells out what each one does, and the destructive
   * one is only offered to the host. It is also what the hardware back gesture
   * lands on, which is why it must never act on its own.
   */
  async promptExit(): Promise<void> {
    const wire = this.store.wire;
    const playing = wire?.phase === 'playing';
    const seat = this.store.seat;
    const body = el('div');
    body.append(el('p', {
      style: 'margin:0 0 4px;color:var(--text-1)',
      text: playing
        ? 'The match is still going. What would you like to do?'
        : 'What would you like to do?',
    }));

    const options: Array<{ label: string; note: string; kind?: 'danger'; run(): void }> = [];
    options.push({
      label: 'Leave the table',
      note: playing
        ? 'Your match ends here. The others carry on, and you can come back on ' +
          'the same link while the table is still up.'
        : 'Go back to the start. Your seat is released.',
      run: () => this.cb.onLeave(),
    });
    if (playing && seat && (wire?.seats.length ?? 0) > 1) {
      options.push({
        label: 'Stop playing, keep watching',
        note: 'You bow out but stay to watch. The others carry on and the bag ' +
              'stretches further between them. You cannot rejoin this match.',
        run: () => this.#act({ t: 'concede' }),
      });
    }
    if (playing && this.store.isHost) {
      options.push({
        label: 'End the match for everyone',
        note: 'It stops now and is scored on whatever is on the boards. Nobody ' +
              'gets the bonus for going out.',
        kind: 'danger',
        run: () => {
          const playerId = this.store.me?.id;
          if (playerId) {
            this.store.send({ t: 'endMatch', playerId, reason: 'the host ended it' } as never);
          }
        },
      });
    }

    await new Promise<void>((resolve) => {
      const list = el('div', { class: 'exits' });
      const close = openModal({
        title: 'Leaving?',
        body,
        actions: [{ label: 'Stay', kind: 'ghost', onClick: () => true }],
        onClose: () => resolve(),
      });
      for (const o of options) {
        const btn = el('button', {
          class: 'exit' + (o.kind === 'danger' ? ' exit--danger' : ''), type: 'button',
        }, el('span', { class: 'exit__label', text: o.label }),
           el('span', { class: 'exit__note', text: o.note }));
        on(btn, 'click', () => { close(); o.run(); });
        list.append(btn);
      }
      body.append(list);
    });
  }

  /**
   * The ? button.
   *
   * It used to open the written rules, which is the least useful of the three
   * things somebody pressing ? might want. Being walked round the actual
   * screen, or watching a hand play a few letters, teaches the game far faster
   * than a page of prose — so those come first and the prose stays available.
   */
  #openHelp(): void {
    const body = el('div', { class: 'exits' });
    const close = openModal({
      title: 'How can I help?',
      body,
      actions: [{ label: 'Close', kind: 'ghost', onClick: () => true }],
    });
    const choice = (label: string, note: string, run: () => void): void => {
      const btn = el('button', { class: 'exit', type: 'button' },
        el('span', { class: 'exit__label', text: label }),
        el('span', { class: 'exit__note', text: note }));
      on(btn, 'click', () => { close(); run(); });
      body.append(btn);
    };
    choice('Walk me round the screen',
      'Points at each part of the screen in turn and says what it is for. ' +
      'Takes about a minute.',
      () => this.startTour());
    choice('Show me how to play',
      'A demonstration you can watch — letters placed, words crossing, the call ' +
      'made, the score added up — with every rule of this match written out ' +
      'underneath.',
      () => openHowToPlay(this.store.wire?.config));
  }

  /** Walk round the game screen. Public so the first match can offer it. */
  startTour(): void {
    // The side panel would cover half of what the tour points at.
    this.side.toggle(false);
    this.#tour?.stop();
    this.#tour = runTour(gameTourSteps(), {
      onDone: () => { this.#tour = null; dismissHint('tour-game'); this.#renderHints(); },
    });
  }

  #tour: TourHandle | null = null;
  #tourOffered = false;
  /** Whether this seat has placed anything yet this match. */
  #hasMoved = false;

  async #confirmLeave(): Promise<void> {
    const wire = this.store.wire;
    const playing = wire?.phase === 'playing';
    const ok = await confirmModal(
      'Leave the table?',
      playing
        ? 'Your match ends here. The others carry on without you, and you can ' +
          'come back with the same link while the table is still up.'
        : 'You will go back to the start.',
      'Leave');
    if (ok) this.cb.onLeave();
  }

  async #confirmEndMatch(): Promise<void> {
    const ok = await confirmModal(
      'End the match for everyone?',
      'It stops now and is scored on whatever is on the boards. Nobody gets ' +
      'the bonus for going out, and every letter left unused still counts ' +
      'against whoever holds it.',
      'End it');
    if (ok) {
      const playerId = this.store.me?.id;
      if (playerId) this.store.send({ t: 'endMatch', playerId, reason: 'the host ended it' } as never);
    }
  }

  async #confirmConcede(): Promise<void> {
    const ok = await confirmModal(
      'Bow out of this match?',
      'You stop playing but stay at the table to watch. The others carry on, ' +
      'and the bag stretches further between them. You cannot rejoin this match.',
      'Bow out');
    if (ok) this.#act({ t: 'concede' });
  }

  async #confirmClear(): Promise<void> {
    const seat = this.store.seat;
    if (!seat || seat.grid.size === 0) return;
    const ok = await confirmModal(
      'Clear your grid?',
      `All ${seat.grid.size} tiles go back to your rack. Nobody else is affected.`,
      'Clear it');
    if (ok) {
      this.board.clearSelection();
      this.#act({ t: 'recallAll' });
      audio.play('drop');
    }
  }

  async #confirmDump(tileId: string): Promise<void> {
    const wire = this.store.wire;
    const seat = this.store.seat;
    if (!wire || !seat) return;
    if (!wire.config.dumpEnabled) return;
    const tile = seat.rack.find((t) => t.id === tileId);
    if (!tile) return;
    const face = faceOf(tile) ?? 'blank';
    const ok = await confirmModal(
      'Trade this letter in?',
      `Put ${face} back in the bag and take ${wire.config.dumpRatio} in its place. ` +
      'You end up with more letters, not fewer.',
      'Trade it in');
    if (ok) this.#act({ t: 'dump', tileId });
  }

  /**
   * The blank dialog, which nobody has to open.
   *
   * A blank is allowed to stay a blank: the engine finds a letter that makes
   * every word through it real, and only objects when there is none. So this
   * is for the player who wants to pin one down on purpose — and where they
   * are already entitled to know whether their words are real, it offers only
   * the letters that would actually leave a legal grid, rather than
   * twenty-six of which twenty-four are traps.
   */
  #promptBlank(tileId: string): void {
    const seat = this.store.seat;
    const wire = this.store.wire;
    const cell = seat
      ? [...seat.grid].find(([, t]) => t.id === tileId)?.[0] ?? null
      : null;
    const narrowed = cell && wire && this.#statusVisible()
      ? lettersFor(seat!.grid, cell, this.store.state.dict, wire.config.minWordLength)
      : null;
    const inferred = cell ? this.store.state.report?.inferred.get(cell) : undefined;

    const body = el('div');
    body.append(el('p', {
      style: 'margin:0 0 12px;color:var(--text-1)',
      text: inferred
        ? `You do not have to choose. This blank is being read as ${inferred}, ` +
          'because that is a letter that makes every word through it real. Pin ' +
          'it down only if you want it to be something else.'
        : 'You do not have to choose. A blank is read as whatever letter makes ' +
          'the words around it work, and the game says so if no letter would. ' +
          'Pin it down only if you want a particular one.',
    }));
    if (narrowed && narrowed.length > 0 && narrowed.length < 26) {
      body.append(el('p', {
        class: 'setting__hint', style: 'margin:0 0 10px',
        text: `Only these would leave a legal grid: ${narrowed.join(' ')}.`,
      }));
    }
    const grid = el('div', { class: 'wordlist' });
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i) as Letter;
      const fits = !narrowed || narrowed.includes(letter);
      const b = el('button', {
        class: 'btn btn--sm', type: 'button', text: letter,
        style: 'min-width:34px;justify-content:center'
          + (fits ? '' : ';opacity:.4'),
        title: fits ? `Pin this blank to ${letter}`
                    : `${letter} would not leave a legal grid`,
      });
      on(b, 'click', () => {
        this.#act({ t: 'assignBlank', tileId, letter });
        audio.play('drop');
        close();
      });
      grid.append(b);
    }
    body.append(grid);
    const close = openModal({
      title: 'This blank',
      body,
      actions: [{
        label: 'Leave it as anything', kind: 'primary',
        onClick: () => { this.#act({ t: 'assignBlank', tileId, letter: null }); },
      }],
    });
  }

  /** Is word colouring permitted right now? */
  #statusVisible(): boolean {
    const wire = this.store.wire;
    if (!wire) return false;
    if (wire.phase === 'finished') return true;
    return wire.config.peek.mode === 'live' || this.#peekActive;
  }

  /** Set for a few seconds after a check is spent. */
  #peekActive = false;
  #peekTimer = 0;

  /**
   * Spend a check.
   *
   * The reveal is gated on the authority actually granting it. The client
   * knows the answer perfectly well — it holds the same dictionary — so it
   * would be trivial to just show it. Doing that would quietly undo the whole
   * anti-guessing mechanic the host configured: budget, window and all. So
   * the request goes out and nothing is shown until a `peeked` event comes
   * back, which is also exactly how it behaves against a real server.
   */
  #pendingCheck = false;
  #pendingCheckTimer = 0;

  #check(): void {
    const wire = this.store.wire;
    const seat = this.store.seat;
    if (!wire || !seat) return;
    if (wire.config.peek.mode === 'live') {
      toast('This match already shows you everything.', 'info');
      return;
    }
    if (wire.config.peek.mode === 'off') {
      toast('No word checking in this match — call it and find out.', 'bad');
      return;
    }
    if (seat.grid.size === 0) { toast('Put some letters down first.', 'info'); return; }
    if (this.#pendingCheck) return;

    this.#pendingCheck = true;
    clearTimeout(this.#pendingCheckTimer);
    // If the request is refused, onError shows why and this simply lapses.
    this.#pendingCheckTimer = window.setTimeout(() => { this.#pendingCheck = false; }, 4000);
    this.store.send({ t: 'peek', seatId: seat.id } as never);
  }

  /** Called only once the authority has granted the check. */
  async #revealCheck(remaining: number): Promise<void> {
    const wire = this.store.wire;
    const seat = this.store.seat;
    if (!wire || !seat) return;

    const report = validateGrid(seat.grid, this.store.state.dict, {
      minWordLength: wire.config.minWordLength, decide: true,
    });
    this.#peekActive = true;
    clearTimeout(this.#peekTimer);
    this.#peekTimer = window.setTimeout(() => {
      this.#peekActive = false;
      this.board.render();
    }, 12_000);
    this.board.render();
    audio.play('peek');

    const body = el('div');
    const bad = report.runs.filter((r) => r.status === 'invalid');
    const good = report.runs.filter((r) => r.status === 'valid');

    body.append(el('p', {
      style: 'margin:0 0 10px;color:var(--text-1)',
      text: bad.length === 0
        ? good.length === 1 ? 'Your word checks out.'
          : `All ${good.length} words check out.`
        : `${bad.length} word${bad.length === 1 ? ' is' : 's are'} not in the list.`,
    }));

    if (bad.length) {
      body.append(el('div', { class: 'wordlist' },
        ...bad.map((r) => el('span', { class: 'wordlist__w wordlist__w--invalid', text: r.word }))));
      body.append(el('div', { style: 'height:12px' }));
    }
    if (good.length) {
      body.append(el('div', { class: 'wordlist' },
        ...good.map((r) => el('span', { class: 'wordlist__w wordlist__w--valid', text: r.word }))));
    }

    const defs = el('div', { style: 'margin-top:14px' });
    body.append(defs);
    const left = Number.isFinite(remaining) ? remaining : 0;
    openModal({
      title: `Check — ${left} left`,
      body,
      actions: [{ label: 'Back to it', kind: 'primary', onClick: () => true }],
    });

    if (wire.config.peek.showDefinition && good.length) {
      defs.append(el('div', { class: 'setting__hint', text: 'Looking up meanings…' }));
      const words = good.slice(0, 6).map((r) => r.word);
      const results = await Promise.all(words.map((w) => lookupDefinition(w)));
      clear(defs);
      let any = false;
      results.forEach((list, i) => {
        if (!list?.length) return;
        any = true;
        const d = list[0];
        if (!d) return;
        defs.append(el('div', { class: 'def' },
          el('b', { text: words[i] as string }),
          el('i', { text: d.partOfSpeech ? ` ${d.partOfSpeech}` : '' }),
          el('span', { text: ` — ${d.text}` })));
      });
      if (!any) {
        defs.append(el('div', {
          class: 'setting__hint',
          text: 'No meanings available right now — the words are still valid for play.',
        }));
      }
    }

    audio.play(bad.length ? 'bad' : 'good');
  }

  // -------------------------------------------------------------------
  // Events from the authority
  // -------------------------------------------------------------------

  handleEvents(events: GameEvent[]): void {
    const wire = this.store.wire;
    const mySeat = wire?.youSeatId;
    for (const ev of events) {
      switch (ev.e) {
        case 'wordFormed':
          if (ev.seatId === mySeat) audio.play('word', { throttleMs: 120 });
          break;
        case 'drew':
          if (ev.seatId === mySeat) audio.play('deal', { throttleMs: 60 });
          break;
        case 'drawAll': {
          const seat = ev.seatId ? wire?.seats.find((s) => s.id === ev.seatId) : null;
          const mine = ev.seatId !== null && ev.seatId === mySeat;
          const phrase = BRAND.callPhrase(ev.drawn);
          const colour = seat?.colour ?? 'var(--accent)';
          audio.play('declare');
          audio.buzz(mine ? 40 : 14);
          // The banner names the reason, because "everyone draws" means
          // something quite different depending on why.
          const banner = ev.reason === 'consensus' ? `ALL STUCK · ${phrase}`
            : ev.reason === 'timer' ? `TIME UP · ${phrase}`
            : mine ? phrase
            : `${seat?.name ?? 'Someone'} · ${phrase}`;
          sweep(banner, colour);
          const c = this.board.contentCentre();
          ring(c.x, c.y, colour);
          announce(
            ev.reason === 'consensus' ? `Everyone was stuck. ${BRAND.callPhraseWords(ev.drawn)}.`
            : ev.reason === 'timer' ? `Time up for the round. ${BRAND.callPhraseWords(ev.drawn)}.`
            : `${mine ? 'You' : seat?.name} called ${BRAND.callPhraseWords(ev.drawn)}. ` +
              `Round ${ev.round}.`);
          break;
        }

        case 'stuckChanged': {
          if (ev.seatId === mySeat) break;      // your own toggle needs no toast
          const seat = wire?.seats.find((s) => s.id === ev.seatId);
          if (ev.stuck) {
            toast(`${seat?.name ?? 'Someone'} is stuck — ${ev.count} of ${ev.needed}`,
              'info', { icon: '✋' });
          }
          break;
        }
        case 'invalidCall':
          if (ev.seatId === mySeat) {
            audio.play('bad');
            audio.buzz(60);
            toast(ev.reason, 'bad', { icon: '✋', ms: 3600 });
          }
          break;
        case 'dumped':
          if (ev.seatId === mySeat) audio.play('deal');
          break;
        case 'bagLow':
          toast('Last letters in the bag — whoever goes out now ends it.', 'info',
            { icon: '🏁', big: true, ms: 3200 });
          audio.play('tick');
          break;
        case 'won': {
          // Going out ends the match and takes the bonus; the score decides
          // who won. Usually the same seat, but not when the bonus is low —
          // so the banner reads off the winner the server actually named
          // rather than assuming it was the caller.
          const seat = wire?.seats.find((s) => s.id === ev.seatId);
          const name = seat?.name ?? 'Someone';
          const tookIt = wire?.winnerSeatId;
          const wentOut = ev.seatId === mySeat;
          audio.play('win');
          sweep(
            tookIt === mySeat ? 'YOU WIN'
              : wentOut ? 'YOU WENT OUT'
              : tookIt === ev.seatId ? `${name} WINS`
              : `${name} WENT OUT`,
            seat?.colour ?? 'var(--accent)');
          if (tookIt === mySeat || wentOut) confetti();
          if (wentOut && ev.bonus > 0) {
            toast(`+${ev.bonus} for using every letter`, 'good', { icon: '⭐' });
          }
          break;
        }
        case 'ended':
          toast(`Match over — ${ev.reason}`, 'info', { big: true });
          break;
        case 'rematch':
          this.#requestedView.clear();
          this.#hasMoved = false;
          this.#resultsShown = false;
          this.#closeResults?.();
          this.#closeResults = null;
          toast('Another match — same table', 'good', { icon: '🔄' });
          break;
        case 'peeked':
          if (ev.seatId === mySeat && this.#pendingCheck) {
            this.#pendingCheck = false;
            clearTimeout(this.#pendingCheckTimer);
            void this.#revealCheck(ev.remaining);
          }
          break;
        default:
          break;
      }
    }
  }

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  render(): void {
    const wire = this.store.wire;
    if (!wire) return;
    const watching = this.store.state.watching;
    const matchKey = `${wire.matchId}:${wire.startedAt ?? ''}:${wire.youSeatId ?? ''}`;
    if (matchKey !== this.#historyFor) {
      this.#historyFor = matchKey;
      this.#history.clear();
    }
    this.#syncUndo();

    // Which grid is on screen: ours, or somebody we are watching.
    if (watching && watching !== wire.youSeatId) {
      const w = this.store.seatById(watching);
      const waiting = !w?.grid;
      this.#watchBanner.hidden = false;
      clear(this.#watchBanner);
      this.#watchBanner.append(el('span', {
        text: waiting ? `Asking to watch ${w?.name ?? '…'}…`
                      : `Watching ${w?.name ?? '…'}`,
      }));
      const stop = el('button', { class: 'btn btn--sm btn--ghost', type: 'button', text: 'back to mine' });
      on(stop, 'click', () => this.store.watch(null));
      this.#watchBanner.append(stop);
      this.board.setSeat(waiting ? null : hydrate(w!), true);
      if (waiting) this.#askToWatch(watching);
    } else {
      this.#watchBanner.hidden = true;
      this.board.setSeat(this.store.seat, wire.phase !== 'playing');
    }

    if (this.store.seat) this.rack.setTiles(this.store.seat.rack);
    this.rack.setTradeEnabled(wire.config.dumpEnabled && wire.phase === 'playing');
    this.side.render(wire, watching);
    this.#renderSwitch();
    this.#nudgeCheck();
    this.#renderStats();
    this.#renderCall();
    this.#renderHints();

    if (wire.roundsTaken !== this.#lastRound) {
      this.#lastRound = wire.roundsTaken;
      this.board.render();
    }
    /*
     * The first time anybody reaches a board, walk them round it.
     *
     * The alternative was deciphering the interface by experiment, which is
     * what the player asked not to have to do. Once per device, recorded with
     * the dismissed hints, and the ? button brings it back.
     */
    if (wire.phase === 'playing' && !this.#tourOffered) {
      this.#tourOffered = true;
      if (!hintDismissed('tour-game')) {
        window.setTimeout(() => {
          if (this.store.wire?.phase === 'playing') this.startTour();
        }, 900);
      }
    }
    if (wire.phase === 'finished' && !this.#resultsShown) {
      this.#resultsShown = true;
      // Kept on this device, for the history in Settings. Only a seated
      // player's view is kept: a spectator played nothing.
      if (wire.youSeatId) {
        try {
          saveGame(recordFrom(wire, this.#reckonings(wire), wire.matchId === 'solo'));
        } catch { /* a history that cannot be written must never break the ending */ }
      }
      setTimeout(() => this.#showResults(), 1200);
    }
    this.#lastVersion = wire.version;
  }

  #requestedView = new Set<string>();

  /**
   * Whose grid is on the board: yours, or one of the others'.
   *
   * Looking at another player's grid used to mean opening the players panel
   * and tapping their card, which nobody found — after a match, "Look at the
   * grids" closed the results and left the player staring at their own board.
   * Now every grid that can be seen is one tap away, named along the foot of
   * the board.
   */
  #switchEl = el('div', { class: 'boardswitch', role: 'tablist', 'aria-label': 'Whose grid to show' });

  #renderSwitch(): void {
    const wire = this.store.wire;
    const box = this.#switchEl;
    clear(box);
    if (!wire || wire.seats.length < 2 || !wire.youSeatId) { box.hidden = true; return; }
    const policy = wire.config.visibility.grids;
    const finished = wire.phase === 'finished';
    const others = wire.seats.filter((s) => s.id !== wire.youSeatId
      && (s.grid !== null || (!finished && policy === 'onRequest')));
    if (!others.length) { box.hidden = true; return; }
    box.hidden = false;
    const watching = this.store.state.watching;
    const chip = (id: string | null, name: string, colour: string): HTMLElement => {
      const on_ = id === null ? !watching || watching === wire.youSeatId : watching === id;
      const b = el('button', {
        class: 'boardswitch__chip', type: 'button', role: 'tab', 'aria-selected': String(on_),
      }, el('span', { class: 'seatcard__dot', style: `background:${colour}` }), el('span', { text: name }));
      on(b, 'click', () => { this.store.watch(id); audio.play('select'); });
      return b;
    };
    const mine = wire.seats.find((s) => s.id === wire.youSeatId);
    box.append(chip(null, 'Mine', mine?.colour ?? 'var(--accent)'));
    for (const s of others) box.append(chip(s.id, s.name, s.colour));
  }
  #syncTools: (() => void) | null = null;

  /**
   * Ask the server for somebody else's grid.
   *
   * Grids are left out of the payload until asked for, so the first render
   * after picking a seat has nothing to draw — the request is what makes the
   * next state include it. Without this the banner appeared and the board
   * stayed empty, which is how watching came to look switched off even when
   * the match allowed it.
   *
   * Asked once per seat, and given a moment to answer: a state update from
   * somebody else's move can easily land in between, and treating that as a
   * refusal would drop the viewer out for no reason.
   */
  #askToWatch(seatId: string): void {
    if (this.#requestedView.has(seatId)) return;
    this.#requestedView.add(seatId);
    if (this.store.wire?.config.visibility.grids !== 'onRequest') {
      toast('This match keeps grids private until it ends.', 'info');
      this.store.watch(null);
      return;
    }
    this.store.send({ t: 'requestView', seatId } as never);
    window.setTimeout(() => {
      if (this.store.state.watching !== seatId) return;
      if (this.store.seatById(seatId)?.grid) return;
      toast('That grid is private in this match.', 'info');
      this.store.watch(null);
    }, 2500);
  }

  #renderStats(): void {
    const wire = this.store.wire;
    if (!wire) return;
    const set = (id: string, text: string, alert = false): void => {
      const node = this.#stats[id];
      if (!node) return;
      if (node.textContent !== text) node.textContent = text;
      node.parentElement?.classList.toggle('stat--alert', alert);
    };
    set('bag', String(wire.bagCount), wire.bagCount < 10);
    set('rounds', String(wire.roundsLeft), wire.roundsLeft <= 1);
    set('hand', String(this.store.seat?.rack.length ?? 0));
    set('tocome', String(wire.tilesToCome), wire.tilesToCome === 0);

    /**
     * The running total, when the table asked for one.
     *
     * `decide` follows {@link #statusVisible}: a score that consulted the word
     * list would drop the moment you laid something invalid, which is a free
     * and unlimited word check. So under a rationed check policy the number
     * counts what you have built and stays quiet about whether it stands.
     */
    const scoreCell = this.#stats['score']?.parentElement;
    const seatNow = this.store.seat;
    if (wire.config.showLiveScore && seatNow) {
      if (scoreCell) scoreCell.hidden = false;
      const total = scoreTotal(seatNow, this.store.state.dict, wire.config,
        this.#statusVisible());
      set('score', String(total), total < 0);
    } else if (scoreCell) {
      scoreCell.hidden = true;
    }

    // When a forced draw is armed, its countdown is the more useful thing to
    // put in the clock slot — and it matters enough to survive onto a phone,
    // where the secondary stats are otherwise hidden.
    const clockEl = this.#stats['clock']?.parentElement;
    const forced = wire.forcedDrawAt;
    if (forced !== null && wire.phase === 'playing') {
      const secs = Math.max(0, Math.round((forced - Date.now()) / 1000));
      set('clock', `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`, secs <= 10);
      const label = clockEl?.querySelector('span');
      if (label) label.textContent = 'draw in';
      clockEl?.classList.remove('stat--secondary');
    } else {
      const elapsed = wire.startedAt ? Date.now() - wire.startedAt : 0;
      const left = wire.config.timeLimitMs > 0 ? wire.config.timeLimitMs - elapsed : elapsed;
      const secs = Math.max(0, Math.round(left / 1000));
      set('clock', `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`,
        wire.config.timeLimitMs > 0 && secs < 60);
      const label = clockEl?.querySelector('span');
      if (label) label.textContent = 'time';
      clockEl?.classList.add('stat--secondary');
    }

    const seat = this.store.seat;
    const mode = wire.config.peek.mode;
    this.#checkBtn.disabled = mode !== 'limited' || (seat?.peeksRemaining ?? 0) <= 0
      || wire.phase !== 'playing';
    const label = this.#checkBtn.firstElementChild as HTMLElement;
    setText(label, mode === 'limited' ? `Is it a word? · ${seat?.peeksRemaining ?? 0}`
      : mode === 'live' ? 'Words marked live' : 'No word checks');
    // Narrow screens get the magnifier and the count only; the full word does
    // not fit beside the stats and the icon buttons.
    const short = this.#checkBtn.querySelector('.check__n') as HTMLElement | null;
    if (short) {
      short.textContent = mode === 'limited' ? String(seat?.peeksRemaining ?? 0)
        : mode === 'live' ? '' : '—';
    }
  }

  #renderCall(): void {
    const wire = this.store.wire;
    const seat = this.store.seat;
    const report = this.store.state.report;
    if (!wire || !seat) return;
    // The button only ever promises what the rules allow. Under 'off' we still
    // know locally whether the grid is legal, but saying so would hand back
    // exactly the feedback the host switched off — so it stays neutral.
    const hideVerdict = wire.config.peek.mode === 'off';
    this.rack.setCallState({
      ready: !hideVerdict && this.store.canCall,
      rackEmpty: seat.rack.length === 0,
      lockedUntil: seat.lockedUntil,
      reason: seat.rack.length > 0
        ? `${seat.rack.length} letter${seat.rack.length === 1 ? '' : 's'} still to place`
        : hideVerdict ? 'Call it and find out'
        : report?.complete ? 'Everything checks out — call it'
        : 'Something is not right yet',
      lastRound: wire.roundsLeft === 0,
      drawPerRound: wire.config.drawPerRound,
      stuck: seat.stuck,
      stuckCount: wire.stuckCount,
      stuckNeeded: wire.stuckNeeded,
      finished: wire.phase === 'finished',
    });
  }

  /** Which match the check button has already been pointed out in. */
  #nudgedFor = '';

  /**
   * Point the check button out once per match, the first time the player has
   * a word on the board — the moment it is worth spending one.
   */
  #nudgeCheck(): void {
    const wire = this.store.wire;
    const seat = this.store.seat;
    if (!wire || !seat || wire.phase !== 'playing' || wire.config.peek.mode !== 'limited') return;
    const key = `${wire.matchId}:${wire.startedAt}`;
    if (this.#nudgedFor === key || seat.peeksRemaining <= 0) return;
    const word = extractRuns(seat.grid).some((r) => r.cells.length >= wire.config.minWordLength);
    if (!word) return;
    this.#nudgedFor = key;
    this.#checkBtn.classList.add('check--nudge');
    window.setTimeout(() => this.#checkBtn.classList.remove('check--nudge'), 3000);
  }

  #renderHints(): void {
    const wire = this.store.wire;
    const seat = this.store.seat;
    if (!wire || !seat || wire.phase !== 'playing' || !prefs().hintsEnabled) {
      this.coach.hide();
      return;
    }
    /*
     * Nothing before the player has done anything.
     *
     * Tips about word checks, swapping letters and the rest were eligible the
     * instant the board appeared — before a single tile had been placed, and
     * on top of the walkthrough. Until the first move the only thing worth
     * saying is how to make one, and even that waits a while: most people
     * work it out, and a tip that arrives before they have had the chance to
     * try is an interruption, not a help.
     */
    if (this.#tour) { this.coach.hide(); return; }
    if (seat.grid.size > 0) this.#hasMoved = true;
    if (!this.#hasMoved) {
      const waited = wire.startedAt ? Date.now() - wire.startedAt : 0;
      this.coach.offer(waited >= FIRST_TIP_AFTER_MS ? [HINTS.firstTile] : []);
      return;
    }

    const report = this.store.state.report;
    const out: Hint[] = [];

    if (seat.grid.size === 0) out.push(HINTS.firstTile);
    if (this.store.canCall && wire.config.peek.mode !== 'off') out.push(HINTS.readyToCall);
    if (wire.roundsLeft === 0) out.push(HINTS.bagLow);

    const problems = report?.problems ?? [];
    if (problems.some((p) => p.kind === 'disconnected')) out.push(HINTS.connect);
    // The adjacency hint is the one that actually teaches the rule, so it only
    // fires when the player has genuinely run two words together.
    if (this.#statusVisible() &&
        problems.some((p) => p.kind === 'invalidWord' && p.run.cells.length >= 6)) {
      out.push(HINTS.adjacency);
    }
    if (seat.grid.size >= 3) out.push(HINTS.panBoard);
    if (seat.grid.size >= 6) out.push(HINTS.longPress, HINTS.marquee, HINTS.doubleTap);
    if (seat.grid.size >= 12) out.push(HINTS.nudge, HINTS.direction);
    if (wire.config.peek.mode === 'limited' && seat.peeksRemaining > 0) {
      out.push(HINTS.checkBudget);
      if (wire.config.peek.windowMs > 0) out.push(HINTS.checkWindow);
    }
    if (wire.config.dumpEnabled && seat.rack.length >= 5) out.push(HINTS.dump);
    if (wire.seats.length > 1 && wire.config.visibility.grids !== 'never') {
      out.push(HINTS.spectate);
    }
    // Only worth teaching where the player can see the number move.
    if (wire.config.showLiveScore) {
      if (seat.grid.size >= 4) out.push(HINTS.crossings);
      if (seat.rack.length >= 3 && wire.roundsTaken > 0) out.push(HINTS.unusedCost);
    }
    // Only worth explaining when the table is not on the familiar two.
    if (wire.config.drawPerRound !== 2) out.push(HINTS.variableDraw);
    // Only offered once somebody has actually had a while to get stuck.
    if (seat.rack.length > 0 && wire.roundsTaken > 0) out.push(HINTS.stuck);
    if (wire.stuckCount > 0 && !seat.stuck) out.push(HINTS.stuckWaiting);

    this.coach.offer(out);
  }

  /**
   * Score every seat the client can actually see.
   *
   * The reckoning is computed here rather than taken off the wire because the
   * grids arrive in the payload anyway once the match is over, and a score the
   * player can recompute from what is on their own screen is one they can
   * check. A seat whose grid is still hidden — a table that switched off the
   * reveal — gets no breakdown rather than a made-up one.
   */
  #reckonings(wire: WireState): Array<{
    seatId: string; name: string; colour: string; you: boolean;
    tilesPlaced: number; tilesInHand: number; complete: boolean;
    wentOut: boolean; score: number;
    breakdown: ScoreBreakdown | null;
  }> {
    return wire.seats.map((s) => {
      const grid = new Map(s.grid ?? []);
      const seen = s.grid !== null;
      const rep = validateGrid(grid, this.store.state.dict, {
        minWordLength: wire.config.minWordLength, decide: true,
      });
      const complete = seen && rep.complete && s.rackCount === 0;
      // Who went out is recorded by the server. It used to be inferred from
      // who won, which dropped the bonus from anybody who went out and still
      // lost on score. (Independent review, R5.)
      const wentOut = s.id === wire.wentOutSeatId;
      const breakdown = seen
        ? scoreSeat({ grid, rack: s.rack ?? [] }, this.store.state.dict,
          wire.config, { wentOut })
        : null;
      return {
        seatId: s.id, name: s.name, colour: s.colour,
        you: s.id === wire.youSeatId,
        tilesPlaced: grid.size, tilesInHand: Math.max(0, s.rackCount),
        complete, wentOut,
        score: breakdown?.total ?? 0,
        breakdown,
      };
    }).sort((a, b) =>
      b.score - a.score ||
      Number(b.complete) - Number(a.complete) ||
      b.tilesPlaced - a.tilesPlaced || a.tilesInHand - b.tilesInHand);
  }

  #showResults(): void {
    const wire = this.store.wire;
    if (!wire) return;
    // Reopened from the RESULTS button: replace, never stack a second copy.
    this.#closeResults?.();
    this.#closeResults = null;
    const rows = this.#reckonings(wire);

    const winner = wire.seats.find((s) => s.id === wire.winnerSeatId);
    const body = el('div');
    body.append(el('p', {
      style: 'margin:0 0 14px;color:var(--text-1)',
      text: !winner ? 'The match is over.'
        : winner.id === wire.youSeatId ? 'You took it.'
        : `${winner.name} took it.`,
    }));

    /**
     * The sum, added up in front of you.
     *
     * Yours first, because that is the one you care about, and then whoever
     * you tap in the table below — which is also how you get to look at how
     * somebody else made their score, not just what it was.
     */
    const sumHost = el('div', { style: 'margin:0 0 14px' });
    let live: { stop(): void } | null = null;
    const showSum = (seatId: string): void => {
      const row = rows.find((r) => r.seatId === seatId);
      clear(sumHost);
      live?.stop();
      live = null;
      if (!row) return;
      if (!row.breakdown) {
        sumHost.append(el('div', {
          class: 'setting__hint',
          text: `${row.name}'s grid stayed private in this match, so there is ` +
                'nothing to add up.',
        }));
        return;
      }
      const r = reckoning({
        seatId: row.seatId, name: row.name, colour: row.colour,
        you: row.you, wentOut: row.wentOut, breakdown: row.breakdown,
      });
      live = r;
      sumHost.append(r.root);
    };
    body.append(sumHost);
    showSum(wire.youSeatId ?? rows[0]?.seatId ?? '');

    body.append(resultsPanel(wire, rows, showSum));
    // Across every match at this table, once there has been more than one.
    const series = seriesPanel(wire);
    if (series) body.append(series);
    body.append(el('p', {
      class: 'setting__hint', style: 'margin-top:14px',
      text: `${wire.roundsTaken} round${wire.roundsTaken === 1 ? '' : 's'} played · ` +
            `${wire.bagCount} letters unused · ${wire.leftoverCount} never dealt.`,
    }));

    const actions: Array<{ label: string; kind?: 'primary' | 'ghost' | 'danger';
      onClick(): void | boolean }> = [
      { label: 'Leave', kind: 'ghost', onClick: () => {
        live?.stop();
        this.cb.onLeave();
      } },
      { label: 'Look at the grids', onClick: () => {
        live?.stop();
        toast(wire.seats.length > 1
          ? 'Tap a name along the bottom of the board to see that grid. RESULTS brings this table back.'
          : 'The RESULTS button brings the final table back.', 'info', { ms: 4200 });
        return true;
      } },
    ];
    // The table survives a match, so playing again should not mean rebuilding
    // it: same seats, same teams, same rules, fresh bag.
    if (this.store.isHost) {
      actions.push({
        label: 'Play again', kind: 'primary',
        onClick: () => {
          live?.stop();
          const playerId = this.store.me?.id;
          if (playerId) this.store.send({ t: 'rematch', playerId } as never);
        },
      });
    } else {
      body.append(el('p', {
        class: 'setting__hint', style: 'margin-top:10px',
        text: 'The host can start another match with the same table.',
      }));
    }

    // Kept so a rematch can take it away: it is appended to the document, not
    // to the screen that gets replaced.
    this.#closeResults = openModal({ title: 'Final table', body, actions });
  }

  // -------------------------------------------------------------------

  #bindShortcuts(): void {
    on(document, 'keydown', (ev) => {
      const e = ev as KeyboardEvent;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === 'z' && !e.shiftKey) { e.preventDefault(); this.undo(); }
        else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); this.redo(); }
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Deliberately no bare letters here: the board owns those while it has
      // focus, which is nearly always. Digits and punctuation can never be a
      // tile, so they are unambiguous.
      switch (e.key) {
        case 'Enter': if (this.store.canCall) { e.preventDefault(); this.#declare(); } break;
        case '/': e.preventDefault(); this.#check(); break;
        case '\\': e.preventDefault(); this.rack.shuffle(); break;
        case '0': e.preventDefault(); this.board.centreOnContent(); break;
        case '1': e.preventDefault(); this.board.setTool('pan'); this.#syncTools?.(); break;
        case '2': e.preventDefault(); this.board.setTool('lasso'); this.#syncTools?.(); break;
        case '3': e.preventDefault(); this.board.setTool('select'); this.#syncTools?.(); break;
        case '-': e.preventDefault(); this.board.zoomBy(1 / 1.2); break;
        case '=': case '+': e.preventDefault(); this.board.zoomBy(1.2); break;
        case '?': e.preventDefault(); openHowToPlay(this.store.wire?.config); break;
        default: break;
      }
    });
    // The first gesture anywhere unlocks audio, which browsers require.
    const unlock = (): void => { audio.unlock(); };
    on(document, 'pointerdown', unlock, { once: true });
    on(document, 'keydown', unlock, { once: true });
  }
}

/** Build a Seat-shaped object from a spectated wire seat. */
function hydrate(w: NonNullable<WireState['seats'][number]>) {
  return {
    id: w.id, name: w.name, colour: w.colour, members: w.members,
    rack: w.rack ?? [], grid: new Map(w.grid ?? []),
    peeksRemaining: 0, lockedUntil: 0, lastPlacementAt: 0,
    finishedAt: w.finishedAt, connectedMembers: w.connectedMembers,
    stuck: w.stuck,
  };
}

export { standings };
export type { Coord };
